/**
 * Service Agreement per client company (finance request 2026-09-16, item 10).
 *
 * Backend: `modules/client-agreements`. The rule lives there: a show created
 * after the feature shipped is flagged "Service Agreement Required" and its
 * estimate cannot be approved / its crew cannot be booked until the company
 * accepts the current version. The screens only mirror that state.
 */
import { API_BASE_URL } from "./baseUrl";
import { http } from "./client";

/** `code` on the 403 body when the gate blocks an action. */
export const SERVICE_AGREEMENT_REQUIRED_CODE = "SERVICE_AGREEMENT_REQUIRED";

export interface ServiceAgreementDocument {
  version: string;
  title: string;
  effectiveFrom: string;
  fileName: string;
}

export interface ServiceAgreementStatus {
  required: boolean;
  currentVersion: string;
  currentTitle: string;
  signedVersion: string | null;
  acceptedAt: string | null;
  signerName: string | null;
  agreementId: string | null;
  outdated: boolean;
  /** false for staff accounts / events without a client company. */
  applicable: boolean;
  /** `?eventId=` only: the show carries the gate flag (created after the feature). */
  gateActive?: boolean;
  /** `?eventId=` only: gate flag AND not signed → estimate approval / crewing blocked. */
  blocked?: boolean;
  companyName?: string | null;
  businessProfileId?: string | null;
  /** Own status only: the caller is a client representative who can accept. */
  canSign?: boolean;
}

export interface ServiceAgreementAcceptanceInput {
  agree: boolean;
  signerName: string;
  signerTitle?: string;
}

/** The blank agreement (public, inline PDF) — safe to put in an iframe / link before login. */
export const serviceAgreementPdfUrl = (version?: string) =>
  `${API_BASE_URL.replace(/\/+$/, "")}/client-agreements/current.pdf${version ? `?version=${encodeURIComponent(version)}` : ""}`;

/**
 * The agreement as a same-origin `blob:` URL for an <iframe>. The API answers with
 * helmet's X-Frame-Options / frame-ancestors, so framing the API URL directly from
 * the app origin is refused by the browser; a blob URL is not.
 */
export const fetchServiceAgreementBlobUrl = async (version?: string): Promise<string | null> => {
  try {
    const response = await fetch(serviceAgreementPdfUrl(version), { method: "GET" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" }));
  } catch {
    return null;
  }
};

/** pdf-service base (same env var the PDF printers use: `VITE_PDF_URL`, e.g. `http://localhost:3000/api`). */
const pdfServiceBase = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  return String(env.VITE_PDF_URL || "").replace(/\/+$/, "");
};

export interface ServiceAgreementPreviewInput {
  version?: string;
  companyName?: string;
  signerName?: string;
  signerTitle?: string;
}

/**
 * Live preview while the client types: every `CLIENT NAME` becomes the company
 * and the signature block shows the typed name/title, watermarked PREVIEW.
 * Rendered by the pdf-service (`POST /api/service-agreement/preview`), which
 * fetches the blank PDF from the backend public route — no token, works at signup.
 * Returns a same-origin blob URL, or null when the service is unavailable.
 */
export const fetchServiceAgreementPreviewBlobUrl = async (
  input: ServiceAgreementPreviewInput,
  signal?: AbortSignal,
): Promise<{ url: string; signaturePage: number | null } | null> => {
  const base = pdfServiceBase();
  if (!base) return null;
  try {
    const response = await fetch(`${base}/service-agreement/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, baseURL: API_BASE_URL }),
      signal,
    });
    if (!response.ok) return null;
    const page = Number(response.headers.get("X-Signature-Page") || "");
    const blob = await response.blob();
    return {
      url: URL.createObjectURL(blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" })),
      signaturePage: Number.isFinite(page) && page > 0 ? page : null,
    };
  } catch {
    return null;
  }
};

export const getServiceAgreementDocument = () =>
  http<ServiceAgreementDocument>({ method: "GET", url: "client-agreements/current", isToken: false });

/** Own company status (client) or the status behind a show (`eventId`, staff/coordinator/owner). */
export const getServiceAgreementStatus = (eventId?: string) =>
  http<ServiceAgreementStatus>({
    method: "GET",
    url: "client-agreements/status",
    params: eventId ? { eventId } : undefined,
    isToken: true,
  });

export const acceptServiceAgreement = (input: ServiceAgreementAcceptanceInput) =>
  http<{ agreementId: string; version: string; acceptedAt: string; companyName: string; required: false }>({
    method: "POST",
    url: "client-agreements/accept",
    data: input,
    isToken: true,
  });

/** Signed copy (original + stamped signature page + certificate) as a blob. */
export const downloadSignedServiceAgreement = (agreementId: string) =>
  http<Blob>({ method: "GET", url: `client-agreements/${agreementId}/signed.pdf`, isToken: true, file: true });

export interface ServiceAgreementAcceptanceRecord {
  _id: string;
  businessProfileId: string | null;
  companyName: string;
  version: string;
  acceptedAt: string;
  signerUserId: string;
  signerName: string;
  signerTitle: string;
  signerEmail: string;
  signerProfile: "primaryContact" | "representative";
  source: "signup" | "app";
  ip: string;
  signedPdf: { key: string; url: string; generatedAt: string } | null;
  isCurrent: boolean;
}

/** Response envelope of the admin list (the backend interceptor flattens `{ items, pagination }` to this). */
export interface ServiceAgreementAcceptancePage {
  success: boolean;
  status: number;
  data: ServiceAgreementAcceptanceRecord[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

/** Admin audit list: 25 per page (backend cap), `search` runs on the server over every acceptance. */
export const listServiceAgreementAcceptances = (params: { page?: number; search?: string } = {}) =>
  http<ServiceAgreementAcceptancePage>({
    method: "GET",
    url: "client-agreements/acceptances",
    params: { page: params.page || 1, ...(params.search ? { search: params.search } : {}) },
    isToken: true,
  });

/** True when an API error is the Service Agreement gate (so screens can offer the "Sign now" path). */
export const isServiceAgreementRequiredError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  return response?.status === 403 && response?.data?.code === SERVICE_AGREEMENT_REQUIRED_CODE;
};
